'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { GatedButton } from '@/src/components/ui/GatedButton';
import { Box, FormField, FormHelp, Heading, Input, Label, Modal, Select, Text } from '@zillow/constellation-10';
import type { HpSearchScopeDto } from '@zg-rentals/ts-hotpads-api-client';
import { useCreateSavedSearch } from '@/src/lib/hooks/query/useSavedSearches';
import { useCurrentUser } from '@/src/lib/hooks/query/useUserQuery';
import {
  buildSavedSearchPayload,
  type SavedSearchAreaBounds,
  type SavedSearchFrequency,
} from '@/src/lib/savedSearches';
import { useFilters } from '@/src/lib/filters';
import {
  getSavedSearchEmailError,
  getSavedSearchNameError,
  SAVED_SEARCH_FREQUENCY_LABEL_KEYS,
  SAVED_SEARCH_FREQUENCY_OPTIONS,
} from './saveSearchFormUtils';

type SaveSearchModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  areaId: string;
  resourceId: string;
  searchSlug: string;
  locationText: string;
  areaBounds: SavedSearchAreaBounds;
  /** The area's typed Zillow id, sent alongside `areas`. */
  searchScope?: HpSearchScopeDto;
};

export function SaveSearchModal({
  isOpen,
  onClose,
  onSaved,
  areaId,
  resourceId,
  searchSlug,
  locationText,
  areaBounds,
  searchScope,
}: SaveSearchModalProps) {
  const t = useTranslations('saveSearchModal');
  const { filters } = useFilters();
  const { data: currentUser } = useCurrentUser();
  const createSavedSearch = useCreateSavedSearch();

  const [name, setName] = useState(locationText);
  const [frequency, setFrequency] = useState<SavedSearchFrequency>('instant');
  const [nameError, setNameError] = useState<string | undefined>();
  const [guestEmail, setGuestEmail] = useState('');
  const [emailError, setEmailError] = useState<string | undefined>();
  const wasOpenRef = useRef(false);

  const isLoggedIn = !!currentUser?.loggedIn;
  const effectiveEmail = isLoggedIn ? (currentUser?.data?.email ?? '') : guestEmail;
  const hasResolvedIdentity = currentUser !== undefined;
  const canSubmit = hasResolvedIdentity && effectiveEmail.trim().length > 0;

  useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false;
      return;
    }
    if (wasOpenRef.current) return;
    wasOpenRef.current = true;
    setName(locationText);
    setFrequency('instant');
    setNameError(undefined);
    setGuestEmail('');
    setEmailError(undefined);
    createSavedSearch.reset();
  }, [createSavedSearch, isOpen, locationText]);

  const mutationError = useMemo(() => {
    const error = createSavedSearch.error;
    if (!error) return undefined;
    const status = (error as Error & { status?: string }).status;
    const isEmailConflict = status === 'EMAIL_ALREADY_EXISTS';
    return isEmailConflict ? t('errorEmailAlreadyExists') : t('errorGeneric');
  }, [createSavedSearch.error, t]);

  const handleSubmit = () => {
    if (!canSubmit) return;

    const trimmedName = name.trim();
    const trimmedEmail = effectiveEmail.trim();
    const nextNameError = getSavedSearchNameError(trimmedName, t);
    const nextEmailError = !isLoggedIn ? getSavedSearchEmailError(trimmedEmail, t) : undefined;

    setNameError(nextNameError);
    setEmailError(nextEmailError);

    if (nextNameError || nextEmailError) return;

    createSavedSearch.mutate(
      buildSavedSearchPayload({
        name: trimmedName,
        email: trimmedEmail,
        frequency,
        resourceId,
        areaId,
        searchSlug,
        filters,
        areaBounds,
        searchScope,
      }),
      {
        onSuccess: () => {
          onSaved();
          onClose();
        },
      },
    );
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={(open) => !open && onClose()} size={{ base: 'fullScreen', lg: 'sm' }}>
      <Modal.Portal>
        <Modal.Backdrop>
          <Modal.Content data-testid="listing-search-save" aria-busy={!hasResolvedIdentity}>
            <Modal.Header>
              <Heading level={2} data-testid="listing-search-save-heading">
                {t('title')}
              </Heading>
            </Modal.Header>
            <Modal.Body>
              <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
                <Text textStyle="body-sm" color="text.subtle">
                  {t('subtitle')}
                </Text>

                <FormField
                  label={<Label>{t('nameLabel')}</Label>}
                  required
                  error={!!nameError}
                  description={nameError ? <FormHelp>{nameError}</FormHelp> : undefined}
                  control={
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder={locationText}
                      size="md"
                    />
                  }
                />

                {!isLoggedIn && (
                  <FormField
                    label={<Label>{t('emailLabel')}</Label>}
                    required
                    error={!!emailError}
                    description={emailError ? <FormHelp>{emailError}</FormHelp> : undefined}
                    control={
                      <Input
                        type="email"
                        value={guestEmail}
                        onChange={(e) => setGuestEmail(e.target.value)}
                        placeholder={t('emailPlaceholder')}
                        size="md"
                      />
                    }
                  />
                )}

                <FormField
                  label={<Label>{t('frequencyLegend')}</Label>}
                  control={
                    <Select
                      value={frequency}
                      onChange={(e) => setFrequency(e.target.value as SavedSearchFrequency)}
                      size="md"
                    >
                      {SAVED_SEARCH_FREQUENCY_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {t(SAVED_SEARCH_FREQUENCY_LABEL_KEYS[option])}
                        </option>
                      ))}
                    </Select>
                  }
                />

                {mutationError && (
                  <Text textStyle="body-sm" css={{ color: 'error' }} role="alert">
                    {mutationError}
                  </Text>
                )}
              </Box>
            </Modal.Body>
            <Modal.Footer>
              <GatedButton
                tone="brand"
                emphasis="filled"
                size="md"
                fluid
                onClick={handleSubmit}
                disabled={!canSubmit || createSavedSearch.isPending}
                gateReason={
                  !createSavedSearch.isPending && hasResolvedIdentity && !effectiveEmail.trim() && t('gateHintEmail')
                }
              >
                {createSavedSearch.isPending ? t('saving') : t('submit')}
              </GatedButton>
            </Modal.Footer>
            <Modal.CloseButton />
          </Modal.Content>
        </Modal.Backdrop>
      </Modal.Portal>
    </Modal.Root>
  );
}
